import { useState, useCallback, useEffect } from 'react';
import { Tabs, Button, InlineStack, Text } from '@shopify/polaris';
import TiptapEditor from '../../../../shared/components/TiptapEditor.jsx';

const LANGUAGE_TABS = [
  { id: 'en', content: 'English' },
  { id: 'nl', content: 'Nederlands' },
  { id: 'fr', content: 'Français' }
];

const LANG_FIELDS = {
  0: 'TechnicalDescription',
  1: 'TechnicalDescriptionNL',
  2: 'TechnicalDescriptionFR'
};

function parseTechDescImages(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    return [];
  }
}

export default function LineTechDescEditor({ line, quotationId, onRefresh, disabled = false }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageError, setImageError] = useState('');
  const [localImages, setLocalImages] = useState(() => parseTechDescImages(line.TechDescImages));
  const [localValues, setLocalValues] = useState({
    TechnicalDescription: line.TechnicalDescription || null,
    TechnicalDescriptionNL: line.TechnicalDescriptionNL || null,
    TechnicalDescriptionFR: line.TechnicalDescriptionFR || null
  });

  // Sync localValues when line prop changes (after save refetch)
  useEffect(() => {
    if (!dirty) {
      setLocalValues({
        TechnicalDescription: line.TechnicalDescription || null,
        TechnicalDescriptionNL: line.TechnicalDescriptionNL || null,
        TechnicalDescriptionFR: line.TechnicalDescriptionFR || null
      });
    }
  }, [line.TechnicalDescription, line.TechnicalDescriptionNL, line.TechnicalDescriptionFR]);
  useEffect(() => {
    setLocalImages(parseTechDescImages(line.TechDescImages));
  }, [line.ID, line.TechDescImages]);

  const handleContentChange = useCallback(
    (json) => {
      const field = LANG_FIELDS[selectedTab];
      setLocalValues((prev) => ({ ...prev, [field]: json }));
      setDirty(true);
    },
    [selectedTab]
  );

  const handleSave = async () => {
    if (disabled) return;
    setSaving(true);
    try {
      await fetch(`/api/project-quotations/${quotationId}/lines/${line.ID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          TechnicalDescription: localValues.TechnicalDescription,
          TechnicalDescriptionNL: localValues.TechnicalDescriptionNL,
          TechnicalDescriptionFR: localValues.TechnicalDescriptionFR
        })
      });
      setDirty(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.log('Error saving tech desc:', err);
    }
    setSaving(false);
  };

  const handleImageUpload = async (file) => {
    if (disabled) return;
    const formData = new FormData();
    formData.append('file', file);
    setImageError('');
    try {
      const res = await fetch(`/api/project-quotations/${quotationId}/lines/${line.ID}/tech-images`, {
        method: 'POST',
        body: formData
      });
      let data = null;
      try {
        data = await res.json();
      } catch (err) {
        data = null;
      }
      if (!res.ok) {
        const message = data?.message || `Image upload failed (${res.status})`;
        setImageError(message);
        return;
      }
      setLocalImages(Array.isArray(data.images) ? data.images : []);
    } catch (err) {
      console.log('Error uploading image:', err);
      setImageError('Image upload failed. Please try again.');
    }
  };

  const handleImageDelete = async (key) => {
    if (disabled) return;
    if (!window.confirm('Delete this image?')) return;
    setImageError('');
    try {
      const res = await fetch(`/api/project-quotations/${quotationId}/lines/${line.ID}/tech-images`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key })
      });
      let data = null;
      try {
        data = await res.json();
      } catch (err) {
        data = null;
      }
      if (!res.ok) {
        const message = data?.message || `Image delete failed (${res.status})`;
        setImageError(message);
        return;
      }
      setLocalImages(Array.isArray(data.images) ? data.images : []);
    } catch (err) {
      console.log('Error deleting image:', err);
      setImageError('Image delete failed. Please try again.');
    }
  };

  const handleImageUpdate = async (key, updates) => {
    if (disabled) return;
    setImageError('');
    try {
      const res = await fetch(`/api/project-quotations/${quotationId}/lines/${line.ID}/tech-images`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, ...updates })
      });
      let data = null;
      try {
        data = await res.json();
      } catch (err) {
        data = null;
      }
      if (!res.ok) {
        const message = data?.message || `Image update failed (${res.status})`;
        setImageError(message);
        return;
      }
      setLocalImages(Array.isArray(data.images) ? data.images : []);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.log('Error updating image:', err);
      setImageError('Image update failed. Please try again.');
    }
  };

  const getImageUrl = useCallback(async (key) => `/api/project-quotations/${quotationId}/lines/${line.ID}/tech-images/view?key=${encodeURIComponent(key)}`, [quotationId, line.ID]);

  const currentField = LANG_FIELDS[selectedTab];

  return (
    <div style={{ padding: '12px 16px', background: '#f9fafb', borderTop: '1px solid #eee' }}>
      <InlineStack align="space-between" blockAlign="center">
        <Text variant="headingSm" as="h4">
          Technical Description — {line.Description || line.Product?.ProductNameNL || 'Line'}
        </Text>
        {dirty && !disabled && (
          <Button variant="primary" size="slim" onClick={handleSave} loading={saving}>
            Save
          </Button>
        )}
      </InlineStack>
      {imageError && (
        <div style={{ marginTop: '8px' }}>
          <Text as="p" tone="critical" variant="bodySm">
            {imageError}
          </Text>
        </div>
      )}
      <div style={{ marginTop: '8px' }}>
        <Tabs tabs={LANGUAGE_TABS} selected={selectedTab} onSelect={setSelectedTab} fitted>
          <div style={{ marginTop: '8px' }}>
            <TiptapEditor
              content={localValues[currentField]}
              onChange={handleContentChange}
              disabled={disabled}
              images={localImages}
              onImageUpload={handleImageUpload}
              onImageDelete={handleImageDelete}
              onImageUpdate={handleImageUpdate}
              getImageUrl={getImageUrl}
            />
          </div>
        </Tabs>
      </div>
    </div>
  );
}
